import { Injectable } from '@angular/core';
import { protectedResources } from '../auth-config';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CloudAccount } from '../models/cloudAccount';
import { Observable, of, retry, map } from 'rxjs';

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class CloudAccountService {
  url = protectedResources.apiCloudAccounts.endpoint;
  private retryCount = 3;
  private retryDelay = 1000; // 1 second delay between retries

  constructor(private httpClient: HttpClient) {}

  private normalizeCloudAccount(item: any): CloudAccount {
    return {
      ...item,
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name,
      externalId: item?.externalId ?? item?.ExternalId,
      cloudProvider: item?.cloudProvider ?? item?.CloudProvider,
      department: item?.department ?? item?.Department,
      departmentSource: item?.departmentSource ?? item?.DepartmentSource,
      sourceConnectors: item?.sourceConnectors ?? item?.SourceConnectors ?? [],
    } as CloudAccount;
  }

  private extractItems<T>(response: any): T[] {
    if (Array.isArray(response)) {
      return response as T[];
    }

    const source = response?.data ?? response?.result ?? response;
    const items = source?.items ?? source?.Items ?? source?.value ?? [];

    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item: any) => this.normalizeCloudAccount(item)) as T[];
  }

  private normalizePaginatedResponse<T>(response: any): PaginatedResponse<T> {
    const source = response?.data ?? response?.result ?? response ?? {};
    const items = this.extractItems<T>(source);

    return {
      items,
      totalCount: Number(source.totalCount ?? source.TotalCount ?? items.length ?? 0),
      pageNumber: Number(source.pageNumber ?? source.PageNumber ?? 0),
      pageSize: Number(source.pageSize ?? source.PageSize ?? items.length ?? 0),
    };
  }

  /**
   * Search cloud accounts by ID or name (server-side search)
   * This is the preferred method for users with many subscriptions
   * @param searchTerm - Subscription ID or name to search for
   */
  public searchCloudAccounts(searchTerm: string): Observable<CloudAccount[]> {
    const params = new HttpParams().set('search', searchTerm.trim());
    return this.httpClient.get<any>(this.url, { params })
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        map((response) => this.extractItems<CloudAccount>(response))
      );
  }

  /**
   * Get a specific cloud account by external ID
   * @param externalId - The subscription ID
   */
  public getCloudAccountById(externalId: string): Observable<CloudAccount> {
    return this.httpClient.get<CloudAccount>(`${this.url}/${externalId}`)
      .pipe(retry({ count: this.retryCount, delay: this.retryDelay }));
  }

  /**
   * Get paginated cloud accounts
   * Use this for browsing when search isn't available
   * @param pageNumber - Page number (0-based)
   * @param pageSize - Number of items per page
   * @param searchTerm - Optional search term
   */
  public getCloudAccountsPaginated(
    pageNumber: number = 0,
    pageSize: number = 50,
    searchTerm?: string
  ): Observable<PaginatedResponse<CloudAccount>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    
    if (searchTerm && searchTerm.trim()) {
      params = params.set('search', searchTerm.trim());
    }

    return this.httpClient.get<any>(this.url, { params })
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        map((response) => this.normalizePaginatedResponse<CloudAccount>(response))
      );
  }

  /**
   * Get cloud accounts by IDs
   * Useful for loading only specific subscriptions (e.g., already selected ones)
   * @param externalIds - Array of subscription IDs
   */
  public getCloudAccountsByIds(externalIds: string[]): Observable<CloudAccount[]> {
    if (!externalIds || externalIds.length === 0) {
      return of([]);
    }

    const params = new HttpParams().set('ids', externalIds.join(','));
    return this.httpClient.get<any>(this.url, { params })
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        map((response) => this.extractItems<CloudAccount>(response))
      );
  }

  /**
   * @deprecated Use searchCloudAccounts or getCloudAccountsPaginated instead
   * This method loads ALL subscriptions and should be avoided for users with many subscriptions
   */
  public getCloudAccounts(): Observable<CloudAccount[]> {
    return this.httpClient.get<any>(this.url)
      .pipe(
        retry({ count: this.retryCount, delay: this.retryDelay }),
        map((response) => this.extractItems<CloudAccount>(response))
      );
  }
}